/**
 * Utilities for determining store open/closed status from API hours.
 */

import type { RestaurantHours, RestaurantHoursException } from '@/types/menuApi'
import {
  formatAbsoluteTimeInZone,
  formatWallTime,
  getEffectiveBusinessTimeZone,
  getIsoDateInTimeZone,
  getRelativeBusinessDate,
  getTimeZoneAbbreviation,
  getViewerTimeZone,
  getZonedDateParts,
  zonedWallTimeToUtcDate,
} from '@/utils/timezone'

export interface StoreStatus {
  isOpen: boolean
  labelKey: string
  labelVars?: Record<string, string>
  label: string
  businessLabel: string
  viewerLabel?: string | null
  businessTimeZone: string
  viewerTimeZone: string
}

/** Parse "HH:MM" to minutes from midnight */
export function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

/** Check if currentMin falls within an open/close window, handling overnight spans */
function isWithinWindow(openMin: number, closeMin: number, currentMin: number): boolean {
  if (openMin <= closeMin) {
    return currentMin >= openMin && currentMin < closeMin
  }
  // Overnight window (e.g., 22:00–08:00): open after openMin OR before closeMin
  return currentMin >= openMin || currentMin < closeMin
}

function buildLabels(
  prefix: 'Open until' | 'Opens at',
  time24: string,
  businessTimeZone: string,
  viewerTimeZone: string,
  now: Date
) {
  const businessAbbr = getTimeZoneAbbreviation(now, businessTimeZone)
  const businessLabel = `${prefix} ${formatWallTime(time24)} ${businessAbbr}`

  const labelKey = prefix === 'Open until' ? 'openUntil' : 'opensAt'
  const labelVars = { time: formatWallTime(time24), abbr: businessAbbr }

  const viewerLabel = viewerTimeZone === businessTimeZone
    ? null
    : `${prefix} ${formatAbsoluteTimeInZone(
        zonedWallTimeToUtcDate(getRelativeBusinessDate(businessTimeZone, 0, now), time24, businessTimeZone),
        viewerTimeZone
      )} your time`

  return { businessLabel, viewerLabel, labelKey, labelVars }
}

/**
 * Determine if a store is currently open based on its hours.
 * Returns { isOpen, label } for display.
 */
export function getStoreStatus(
  hours?: RestaurantHours[],
  options?: {
    hoursExceptions?: RestaurantHoursException[]
    businessTimeZone?: string | null
    now?: Date
  }
): StoreStatus {
  const viewerTimeZone = getViewerTimeZone()
  const businessTimeZone = getEffectiveBusinessTimeZone(
    options?.businessTimeZone,
    undefined
  )
  const now = options?.now ?? new Date()
  const businessNow = getZonedDateParts(now, businessTimeZone)

  if (!hours || hours.length === 0) {
    return {
      isOpen: false,
      labelKey: 'hoursNotSet',
      label: 'Hours not set',
      businessLabel: 'Hours not set',
      viewerLabel: null,
      businessTimeZone,
      viewerTimeZone,
    }
  }

  const apiDay = businessNow.weekday
  const todayIso = getIsoDateInTimeZone(now, businessTimeZone)
  const todayException = options?.hoursExceptions?.find((entry) => entry.date === todayIso)

  if (todayException) {
    if (todayException.is_closed) {
      return {
        isOpen: false,
        labelKey: todayException.label ? 'closedTodayWithLabel' : 'closedToday',
        labelVars: todayException.label ? { label: todayException.label } : undefined,
        label: 'Closed today',
        businessLabel: todayException.label
          ? `Closed today - ${todayException.label}`
          : 'Closed today',
        viewerLabel: null,
        businessTimeZone,
        viewerTimeZone,
      }
    }

    if (todayException.is_24_hours) {
      return {
        isOpen: true,
        labelKey: 'open24Hours',
        label: 'Open 24 hours',
        businessLabel: 'Open 24 hours',
        viewerLabel: null,
        businessTimeZone,
        viewerTimeZone,
      }
    }

    if (todayException.open_time && todayException.close_time) {
      const currentMinutes = businessNow.hour * 60 + businessNow.minute
      const openMin = timeToMinutes(todayException.open_time)
      const closeMin = timeToMinutes(todayException.close_time)
      const isOpen = isWithinWindow(openMin, closeMin, currentMinutes)
      const isOvernight = openMin > closeMin
      const labels = isOpen
        ? buildLabels('Open until', todayException.close_time, businessTimeZone, viewerTimeZone, now)
        : (!isOvernight && currentMinutes < openMin)
          ? buildLabels('Opens at', todayException.open_time, businessTimeZone, viewerTimeZone, now)
          : { businessLabel: 'Closed', viewerLabel: null, labelKey: 'closed', labelVars: undefined }

      return {
        isOpen,
        labelKey: labels.labelKey ?? 'closed',
        labelVars: labels.labelVars,
        label: labels.businessLabel,
        businessLabel: labels.businessLabel,
        viewerLabel: labels.viewerLabel,
        businessTimeZone,
        viewerTimeZone,
      }
    }
  }

  const today = hours.find((h) => h.day_of_week === apiDay)
  const currentMinutes = businessNow.hour * 60 + businessNow.minute

  if (!today || today.is_closed) {
    // Don't return yet — yesterday's overnight window might carry into today (see below)
  } else if (today.is_24_hours) {
    return {
      isOpen: true,
      labelKey: 'open24Hours',
      label: 'Open 24 hours',
      businessLabel: 'Open 24 hours',
      viewerLabel: null,
      businessTimeZone,
      viewerTimeZone,
    }
  } else if (!today.open_time || !today.close_time) {
    // No valid times — fall through to yesterday overnight check
  } else {
    const openMin = timeToMinutes(today.open_time)
    const closeMin = timeToMinutes(today.close_time)

    if (isWithinWindow(openMin, closeMin, currentMinutes)) {
      const labels = buildLabels('Open until', today.close_time, businessTimeZone, viewerTimeZone, now)
      return {
        isOpen: true,
        labelKey: labels.labelKey,
        labelVars: labels.labelVars,
        label: labels.businessLabel,
        businessLabel: labels.businessLabel,
        viewerLabel: labels.viewerLabel,
        businessTimeZone,
        viewerTimeZone,
      }
    }

    const isOvernight = openMin > closeMin
    if (!isOvernight && currentMinutes < openMin) {
      const labels = buildLabels('Opens at', today.open_time, businessTimeZone, viewerTimeZone, now)
      return {
        isOpen: false,
        labelKey: labels.labelKey,
        labelVars: labels.labelVars,
        label: labels.businessLabel,
        businessLabel: labels.businessLabel,
        viewerLabel: labels.viewerLabel,
        businessTimeZone,
        viewerTimeZone,
      }
    }
  }

  // Check if yesterday had an overnight window that carries into today
  const yesterdayApiDay = (apiDay - 1 + 7) % 7
  const yesterday = hours.find((h) => h.day_of_week === yesterdayApiDay)
  if (yesterday && !yesterday.is_closed && yesterday.open_time && yesterday.close_time) {
    const yOpenMin = timeToMinutes(yesterday.open_time)
    const yCloseMin = timeToMinutes(yesterday.close_time)
    if (yOpenMin > yCloseMin && currentMinutes < yCloseMin) {
      // Still within yesterday's overnight window
      const labels = buildLabels('Open until', yesterday.close_time, businessTimeZone, viewerTimeZone, now)
      return {
        isOpen: true,
        labelKey: labels.labelKey,
        labelVars: labels.labelVars,
        label: labels.businessLabel,
        businessLabel: labels.businessLabel,
        viewerLabel: labels.viewerLabel,
        businessTimeZone,
        viewerTimeZone,
      }
    }
  }

  return {
    isOpen: false,
    labelKey: 'closed',
    label: 'Closed',
    businessLabel: 'Closed',
    viewerLabel: null,
    businessTimeZone,
    viewerTimeZone,
  }
}
